import axios from 'axios';
import { getAccessToken, getRefreshToken, setAuthSession, clearAuthSession } from '../utils/tokenStorage';

const baseURL = process.env.REACT_APP_API_BASE_URL;

export const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let refreshQueue = [];

const isAuthPath = (url) => typeof url === 'string' && url.startsWith('/api/auth/');

const redirectToLogin = () => {
  if (typeof window === 'undefined') return;

  // If using hash-based routing (HashRouter), force hash to root login route
  if (window.location.hash !== '#/' && window.location.hash !== '#') {
    window.location.hash = '#/';
    return;
  }

  // Fallback for BrowserRouter-style routing
  if (window.location.pathname !== '/') {
    window.location.replace('/');
  }
};

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  refreshQueue = [];
};

apiClient.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();

    if (accessToken) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error || {};
    const status = response?.status;

    console.log('status', status);

    // Skip refresh logic for auth endpoints themselves (login/logout/register/etc.)
    const shouldAttemptRefresh =
      status === 401 &&
      config &&
      !config._retry &&
      !config.__isRefreshRequest &&
      !isAuthPath(config.url);

    if (shouldAttemptRefresh) {
      config._retry = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearAuthSession();
        redirectToLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((newAccessToken) => {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient(config);
          })
          .catch((queueError) => Promise.reject(queueError));
      }

      isRefreshing = true;

      try {
        const refreshResponse = await refreshClient.post(
          '/api/auth/refresh-token',
          { refreshToken },
          { __isRefreshRequest: true }
        );

        const tokens = refreshResponse?.data?.data;

        if (!tokens?.accessToken) {
          throw new Error('Unable to refresh authentication tokens');
        }

        setAuthSession(tokens);
        processQueue(null, tokens.accessToken);

        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        return apiClient(config);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthSession();
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (response?.status === 401) {
     
      clearAuthSession();
      redirectToLogin();
    }

    // Handle forbidden / permission-related errors by logging out and redirecting
    if (response?.status === 403) {
      clearAuthSession();
      redirectToLogin();
    }

    return Promise.reject(error);
  }
);

export default apiClient;

